import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EntityCombobox } from '@/components/shared/entity-combobox';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { SwitchField } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { formatDate } from '@/utils/format';

import type { MemberFormState, MemberSourceKind } from './member-form-state';

interface ProgramOption {
  id: string;
  name: string;
  kind: 'service' | 'program';
}

interface OccurrenceOption {
  id: string;
  occurrenceDate: string;
}

interface MemberSearchOption {
  id: string;
  firstName: string;
  lastName: string;
}

interface PaginatedResponse<T> {
  data: T[];
  page: { page: number; totalPages: number };
}

interface MemberSourceFieldsProps {
  value: MemberFormState;
  onChange: (next: MemberFormState) => void;
  popoverPortal?: 'dialog' | 'body';
}

const SOURCE_KINDS: MemberSourceKind[] = [
  'gathering',
  'invited',
  'walk_in',
  'online',
  'social_media',
  'transfer',
  'other',
];

const fetchAllPages = async <T,>(path: string): Promise<T[]> => {
  const first = await api.get<PaginatedResponse<T>>(`${path}&page=1`);
  const rows = [...first.data];
  for (let page = 2; page <= first.page.totalPages; page += 1) {
    const next = await api.get<PaginatedResponse<T>>(`${path}&page=${page}`);
    rows.push(...next.data);
  }
  return rows;
};

export const MemberSourceFields = ({
  value,
  onChange,
  popoverPortal = 'dialog',
}: MemberSourceFieldsProps) => {
  const { t } = useTranslation('people');
  const [memberQuery, setMemberQuery] = useState('');

  const set = <K extends keyof MemberFormState>(key: K, next: MemberFormState[K]) => {
    onChange({ ...value, [key]: next });
  };

  const programs = useQuery({
    queryKey: ['programs', 'member-source', 'all'],
    queryFn: () => fetchAllPages<ProgramOption>('/programs?pageSize=100&sort=name&direction=asc'),
  });

  const occurrences = useQuery({
    queryKey: ['occurrences', 'member-source', value.sourceProgramId, 'all'],
    queryFn: () =>
      fetchAllPages<OccurrenceOption>(
        `/occurrences?programId=${value.sourceProgramId}&pageSize=100&sort=occurrenceDate&direction=desc`,
      ),
    enabled: Boolean(value.sourceProgramId),
  });

  const memberSearch = useQuery({
    queryKey: ['people', 'member-source-search', memberQuery],
    queryFn: () =>
      api.get<{ data: MemberSearchOption[] }>(
        `/people/members?q=${encodeURIComponent(memberQuery)}&pageSize=20&status=active`,
      ),
    enabled: value.sourceKind === 'invited' && value.invitedByIsMember,
  });

  const programOptions =
    programs.data?.map((row) => ({
      value: row.id,
      label: row.kind === 'service' ? row.name : `${row.name} (${t('members.source.programKind')})`,
    })) ?? [];

  const occurrenceOptions =
    occurrences.data?.map((row) => ({
      value: row.id,
      label: formatDate(row.occurrenceDate),
    })) ?? [];

  const memberOptions = useMemo(
    () =>
      (memberSearch.data?.data ?? []).map((row) => ({
        id: row.id,
        label: `${row.firstName} ${row.lastName}`.trim(),
      })),
    [memberSearch.data?.data],
  );

  const selectedInviter = memberOptions.find((row) => row.id === value.sourceInvitedByMemberId);

  const showGatheringFields = value.sourceKind === 'gathering';
  const showInvitedFields = value.sourceKind === 'invited';
  const showOtherDetailField = value.sourceKind === 'other';

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="flex flex-col gap-1.5 sm:col-span-2">
        <Label>{t('members.form.sourceKind')}</Label>
        <Listbox
          value={value.sourceKind}
          onChange={(next) => {
            const kind = next as MemberSourceKind | null;
            onChange({
              ...value,
              sourceKind: kind,
              sourceProgramId: kind === 'gathering' ? value.sourceProgramId : null,
              sourceOccurrenceId: kind === 'gathering' ? value.sourceOccurrenceId : null,
              sourceInvitedByMemberId: kind === 'invited' ? value.sourceInvitedByMemberId : null,
              sourceDetail:
                kind === 'other' || (kind === 'invited' && !value.invitedByIsMember)
                  ? value.sourceDetail
                  : '',
            });
          }}
          options={SOURCE_KINDS.map((kind) => ({
            value: kind,
            label: t(`members.sourceKind.${kind}`),
          }))}
          clearable
          clearLabel={t('members.form.clear')}
          popoverPortal={popoverPortal}
        />
      </div>

      {showGatheringFields ? (
        <>
          <div className="flex flex-col gap-1.5">
            <Label>{t('members.form.sourceProgram')}</Label>
            <Listbox
              value={value.sourceProgramId}
              onChange={(programId) => {
                onChange({
                  ...value,
                  sourceProgramId: programId,
                  sourceOccurrenceId: null,
                });
              }}
              options={programOptions}
              placeholder={t('members.form.sourceProgramPlaceholder')}
              popoverPortal={popoverPortal}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{t('members.form.sourceOccurrence')}</Label>
            <Listbox
              value={value.sourceOccurrenceId}
              onChange={(occurrenceId) => {
                const selected = occurrences.data?.find((row) => row.id === occurrenceId);
                onChange({
                  ...value,
                  sourceOccurrenceId: occurrenceId,
                  firstContactOn: selected?.occurrenceDate ?? value.firstContactOn,
                });
              }}
              options={occurrenceOptions}
              placeholder={t('members.form.sourceOccurrencePlaceholder')}
              disabled={!value.sourceProgramId}
              clearable
              clearLabel={t('members.form.clear')}
              popoverPortal={popoverPortal}
            />
          </div>
        </>
      ) : null}

      {showInvitedFields ? (
        <div className="flex flex-col gap-3 sm:col-span-2">
          <SwitchField
            id="invited-by-is-member"
            label={t('members.form.invitedByIsMember')}
            checked={value.invitedByIsMember}
            onCheckedChange={(checked) =>
              onChange({
                ...value,
                invitedByIsMember: checked,
                sourceInvitedByMemberId: checked ? value.sourceInvitedByMemberId : null,
                sourceDetail: checked ? '' : value.sourceDetail,
              })
            }
          />
          {value.invitedByIsMember ? (
            <div className="flex flex-col gap-1.5">
              <Label>{t('members.form.invitedByMember')}</Label>
              <EntityCombobox
                value={value.sourceInvitedByMemberId}
                onChange={(memberId) => set('sourceInvitedByMemberId', memberId)}
                options={memberOptions}
                selected={selectedInviter ?? null}
                onQueryChange={setMemberQuery}
                loading={memberSearch.isFetching}
                placeholder={t('members.form.invitedByMemberPlaceholder')}
                emptyMessage={t('members.form.invitedByMemberEmpty')}
                clearLabel={t('members.form.clear')}
                popoverPortal={popoverPortal}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <Label>{t('members.form.invitedByName')}</Label>
              <Input
                value={value.sourceDetail}
                onChange={(event) => set('sourceDetail', event.target.value)}
                placeholder={t('members.form.invitedByNamePlaceholder')}
              />
            </div>
          )}
        </div>
      ) : null}

      {showOtherDetailField ? (
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <Label>{t('members.form.sourceDetail')}</Label>
          <Input
            value={value.sourceDetail}
            onChange={(event) => set('sourceDetail', event.target.value)}
            placeholder={t('members.form.sourceDetailPlaceholder')}
          />
        </div>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <Label>{t('members.form.firstContactOn')}</Label>
        <DatePicker
          value={value.firstContactOn}
          onChange={(next) => set('firstContactOn', next)}
          popoverPortal={popoverPortal}
        />
      </div>
    </div>
  );
};
